import { EyeOutlined } from '@ant-design/icons'
import {
  Tag,
  Button,
  Table,
  Card,
  Modal,
  Tooltip,
  Typography,
  message,
  Progress,
} from '@surecompanies/core_components'

import { useQueryClient } from '@tanstack/react-query'
import { useRouterContext } from 'components/RouterContext'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { TableProps } from '@surecompanies/core_components'
import type { ColumnType, SorterResult } from 'antd/es/table/interface'
import { convertToLocalFromUTCISODate } from 'helpers/datetime'
import type { PlanManagementTaskDto } from '../api/dto/plan-management-task.dto'
import { PlanManagementTaskStatusEnum } from '../api/enums/plan-management-task-status.enum'
import styles from '../PlanManagementV2.module.scss'
import usePlanDataSnapshotList, {
  SortField,
} from '../hooks/usePlanDataSnapshotList'
import { deleteTask } from '../api/plan-management-tasks-api'
import { planReviewPath } from '../paths'
import FetchPlanModal from './FetchPlanModal'

const TOOLTIP_OVERLAY_STYLE = {
  maxWidth: 'none',
  whiteSpace: 'nowrap',
} as const

const POINTER_STYLE = { cursor: 'pointer' } as const

function renderStatusTag(status: PlanManagementTaskStatusEnum) {
  switch (status) {
    case PlanManagementTaskStatusEnum.PENDING_FETCH:
      return <Tag color="processing">Pending Fetch</Tag>
    case PlanManagementTaskStatusEnum.FAIL_FETCH:
      return <Tag color="error">Fail Fetch</Tag>
    case PlanManagementTaskStatusEnum.FAIL_SYNC:
      return <Tag color="error">Fail Sync</Tag>
    case PlanManagementTaskStatusEnum.MIGRATING:
      return <Tag color="processing">Migrating</Tag>
    case PlanManagementTaskStatusEnum.MIGRATE_FAILED:
      return <Tag color="error">Migrate Failed</Tag>
    case PlanManagementTaskStatusEnum.MIGRATED:
      return <Tag color="purple">Migrated</Tag>
    case PlanManagementTaskStatusEnum.PENDING_COMPARE:
      return <Tag color="processing">Analyzing</Tag>
    case PlanManagementTaskStatusEnum.FAIL_COMPARE:
      return <Tag color="error">Fail Analyze</Tag>
    case PlanManagementTaskStatusEnum.SUCCESS:
      return <Tag color="success">Success</Tag>
    default:
      return <Tag>{status}</Tag>
  }
}

function renderStatus(row: PlanManagementTaskDto) {
  const tag = renderStatusTag(row.status)
  const isPending = row.status === PlanManagementTaskStatusEnum.PENDING_FETCH
  if (!isPending || row.progress == null) return tag
  const percent = Math.round(row.progress)
  return (
    <div className={styles.statusProgress}>
      {tag}
      <div className={styles.statusBarRow}>
        <div className={styles.statusProgressBar}>
          <Progress
            percent={percent}
            size="small"
            showInfo={false}
            strokeWidth={6}
            status="active"
          />
        </div>
        <span className={styles.statusPercent}>{percent}%</span>
      </div>
    </div>
  )
}

export default function PlanSnapshotsTable() {
  const { router } = useRouterContext()
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const closeModal = useCallback(() => setIsModalOpen(false), [])
  const [deleteTarget, setDeleteTarget] =
    useState<PlanManagementTaskDto | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDateShiftClick = useCallback(
    (row: PlanManagementTaskDto, e: React.MouseEvent) => {
      if (!e.shiftKey) return
      e.stopPropagation()
      setDeleteTarget(row)
    },
    []
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await deleteTask(deleteTarget.id)
      await queryClient.invalidateQueries({
        queryKey: ['planDataSnapshotList'],
      })
      setDeleteTarget(null)
      message.success('Task deleted')
    } catch {
      message.error('Failed to delete task')
    } finally {
      setIsDeleting(false)
    }
  }, [deleteTarget, queryClient])

  const {
    data,
    total,
    isLoading,
    error,
    page,
    pageSize,
    sort,
    order,
    hasPendingTask,
    handlePageChange,
    handleSortChange,
  } = usePlanDataSnapshotList()

  const columns = useMemo<ColumnType<PlanManagementTaskDto>[]>(
    () => [
      {
        title: 'Date received',
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: false,
        sortOrder:
          sort === 'createdAt'
            ? order === 'ASC'
              ? 'ascend'
              : 'descend'
            : undefined,
        render: (value: string, row: PlanManagementTaskDto) =>
          value ? (
            <Tooltip
              title={`ID: ${row.id}`}
              placement="topLeft"
              overlayStyle={TOOLTIP_OVERLAY_STYLE}
            >
              <span
                style={POINTER_STYLE}
                onClick={(e) => handleDateShiftClick(row, e)}
              >
                {convertToLocalFromUTCISODate(value)}
              </span>
            </Tooltip>
          ) : (
            ''
          ),
      },
      {
        title: 'Plans & Rates',
        dataIndex: 'planAndRateSummary',
        key: 'planAndRateSummary',
        render: (value: number | null) =>
          value != null ? value.toLocaleString() : '—',
      },
      {
        title: 'Plan Benefits',
        dataIndex: 'planBenefitSummary',
        key: 'planBenefitSummary',
        render: (value: number | null) =>
          value != null ? value.toLocaleString() : '—',
      },
      {
        title: 'Service Areas',
        dataIndex: 'serviceAreaSummary',
        key: 'serviceAreaSummary',
        render: (value: number | null) =>
          value != null ? value.toLocaleString() : '—',
      },
      {
        title: 'Smoker Rates',
        dataIndex: 'smokerRateSummary',
        key: 'smokerRateSummary',
        render: (value: number | null) =>
          value != null ? value.toLocaleString() : '—',
      },
      {
        title: 'Locations',
        dataIndex: 'locationSummary',
        key: 'locationSummary',
        render: (value: number | null) =>
          value != null ? value.toLocaleString() : '—',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 200,
        align: 'center' as const,
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: false,
        sortOrder:
          sort === 'status'
            ? order === 'ASC'
              ? 'ascend'
              : 'descend'
            : undefined,
        render: (_: unknown, row: PlanManagementTaskDto) => renderStatus(row),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_: unknown, row: PlanManagementTaskDto) => {
          const isDisabled =
            row.status !== PlanManagementTaskStatusEnum.SUCCESS &&
            row.status !== PlanManagementTaskStatusEnum.PENDING_COMPARE &&
            row.status !== PlanManagementTaskStatusEnum.FAIL_COMPARE &&
            row.status !== PlanManagementTaskStatusEnum.MIGRATED &&
            row.status !== PlanManagementTaskStatusEnum.MIGRATING &&
            row.status !== PlanManagementTaskStatusEnum.MIGRATE_FAILED
          return (
            <Button
              type="primary"
              icon={<EyeOutlined />}
              disabled={isDisabled}
              onClick={() => router.push(planReviewPath(row.id))}
            >
              Review
            </Button>
          )
        },
      },
    ],
    [sort, order, router, handleDateShiftClick]
  )

  const handleTableChange: TableProps<PlanManagementTaskDto>['onChange'] =
    useCallback(
      (_, __, sorter, extra) => {
        if (extra?.action === 'sort') {
          const s = sorter as SorterResult<PlanManagementTaskDto>
          if (s.field && s.order) {
            handleSortChange(
              s.field as SortField,
              s.order === 'ascend' ? 'ASC' : 'DESC'
            )
          } else if (sort === 'createdAt' && order === 'DESC') {
            handleSortChange('createdAt', 'ASC')
          } else {
            handleSortChange('createdAt', 'DESC')
          }
        }
      },
      [handleSortChange, sort, order]
    )

  useEffect(() => {
    if (error) {
      console.error('Failed to load plan data snapshots', error)
      message.error('An error occurred. Please try again.')
    }
  }, [error])

  return (
    <Card className={styles.planSnapshotsCard}>
      <FetchPlanModal
        open={isModalOpen}
        onOk={closeModal}
        onCancel={closeModal}
      />

      <Modal
        open={!!deleteTarget}
        title="Delete task?"
        centered
        maskClosable={false}
        onCancel={() => setDeleteTarget(null)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setDeleteTarget(null)}
            disabled={isDeleting}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            loading={isDeleting}
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>,
        ]}
      >
        <p>
          This will permanently delete task <strong>{deleteTarget?.id}</strong>{' '}
          (plan year {deleteTarget?.planYear}) and all associated S3 files. This
          cannot be undone.
        </p>
      </Modal>

      <div className={styles.tableHeader}>
        <Typography.Title level={5} className={styles.tableHeaderTitle}>
          Plan data snapshots
        </Typography.Title>
        <Tooltip
          title={hasPendingTask ? 'A task is already in progress' : undefined}
        >
          <Button
            disabled={hasPendingTask}
            onClick={() => setIsModalOpen(true)}
          >
            Fetch another
          </Button>
        </Tooltip>
      </div>

      <Table
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} items`,
          onChange: handlePageChange,
        }}
        sortDirections={['ascend', 'descend']}
        showSorterTooltip={false}
        locale={{ emptyText: 'No Data' }}
      />
    </Card>
  )
}
